import moment from 'moment';
import { nullSafeGet, nullSafeGetOrElse, nullSafeGetWithZero } from './DataAccessUtils';
import { PERMISSION_NAMES, isBuyerOrInternalTech, isUserAllowedToAccess } from './AuthUtils';
import { COMPLETED_CHECKOUT_VALUES } from '../constants/ServiceCallCheckOutOptions';

/**
 * Refrigerant tracking — EPA Section 608 / AIM Act compliance fields on the asset.
 *
 * The asset form writes four values; everything regulated that is DERIVED from them (leak rate,
 * compliance band, CO2e) is computed server-side from the append-only event ledger and is never
 * recomputed here.
 */

/* ── Redux target collections ──────────────────────────────────────────────── */

export const REFRIGERANT_TYPE_PICKER = 'refrigerantTypePicker';
/** The Refrigerant Tracking ▸ Assets index. A separate slice from `assetsIndex` so the two lists
 *  keep their own filters and paging — this one is permanently scoped to tracked appliances. */
export const REFRIGERANT_ASSETS_TC = 'refrigerantAssetsIndex';
/** The Refrigerant Tracking ▸ Event Log index. */
export const REFRIGERANT_EVENTS_TC = 'refrigerantEventsIndex';
export const REFRIGERANT_EVENTS_ADVANCED_FILTERS = 'refrigerantEventsAdvancedFilters';
/** One appliance's ledger, on the asset detail tab. */
export const REFRIGERANT_ASSET_LEDGER_TC = 'refrigerantAssetLedger';
/**
 * The same appliance's ledger on the printable servicing record. A SEPARATE collection from
 * `REFRIGERANT_ASSET_LEDGER_TC` on purpose: the record sorts oldest-first and the tab newest-first,
 * so sharing one would leave whichever screen mounted second reordering the other's list under it.
 */
export const REFRIGERANT_SERVICING_RECORD_TC = 'refrigerantServicingRecord';
/** Contacts picker inside the log-event drawer. */
export const REFRIGERANT_TECHNICIAN_PICKER = 'refrigerantTechnicianPicker';
/** Asset picker inside the log-event drawer, when it is opened without an appliance. */
export const REFRIGERANT_EVENT_ASSET_PICKER = 'refrigerantEventAssetPicker';
/** Work-order picker inside the log-event drawer. */
export const REFRIGERANT_EVENT_WORK_ORDER_PICKER = 'refrigerantEventWorkOrderPicker';

/* ── Enums. Case-sensitive; these exact strings cross the wire. ────────────── */

export const APPLIANCE_CATEGORIES = {
	COMFORT_COOLING: 'ComfortCooling',
	COMMERCIAL_REFRIGERATION: 'CommercialRefrigeration',
	INDUSTRIAL_PROCESS: 'IndustrialProcess',
	OTHER_NON_REGULATED: 'OtherNonRegulated',
};

const APPLIANCE_CATEGORY_LABELS = {
	[APPLIANCE_CATEGORIES.COMFORT_COOLING]: 'Comfort Cooling',
	[APPLIANCE_CATEGORIES.COMMERCIAL_REFRIGERATION]: 'Commercial Refrigeration',
	[APPLIANCE_CATEGORIES.INDUSTRIAL_PROCESS]: 'Industrial Process Refrigeration (IPR)',
	[APPLIANCE_CATEGORIES.OTHER_NON_REGULATED]: 'Other / Non-Regulated',
};

/**
 * Mirrors `utils.RefrigerantLeakRateUtil.RefrigerantThresholds.statutoryByCategory`. Presentation
 * only — the server never trusts a threshold sent from a client.
 *
 * `OtherNonRegulated` is deliberately absent rather than mapped to 0: it means "log the servicing,
 * do not band it", and a 0% threshold would band everything as exceeded.
 */
const STATUTORY_THRESHOLD_PERCENT_BY_CATEGORY = {
	[APPLIANCE_CATEGORIES.COMFORT_COOLING]: 10,
	[APPLIANCE_CATEGORIES.COMMERCIAL_REFRIGERATION]: 20,
	[APPLIANCE_CATEGORIES.INDUSTRIAL_PROCESS]: 30,
};

/**
 * The picker's options, with each category's EFFECTIVE threshold in the label.
 *
 * `thresholdOverrides` comes from the buyer's `refrigerantConfig`. Reading it matters: a buyer who
 * has raised commercial refrigeration to 24% would otherwise be shown "20% leak rate threshold"
 * while the server bands at 24 — the label would be quietly lying about a regulated number in the
 * one form where the user picks it.
 */
export const getApplianceCategoryOptions = (thresholdOverrides) => {
	const overrideByCategory = (thresholdOverrides || []).reduce((accumulator, override) => {
		const category = nullSafeGet('applianceCategory', override);
		const thresholdPercent = nullSafeGetWithZero('leakRateThresholdPercent', override);
		return category && thresholdPercent !== null && thresholdPercent !== undefined
			? { ...accumulator, [category]: thresholdPercent }
			: accumulator;
	}, {});

	return Object.keys(APPLIANCE_CATEGORY_LABELS).map((category) => {
		const label = APPLIANCE_CATEGORY_LABELS[category];
		// Bracket access, not `nullSafeGetOrElse(category, ...)`: the accessor splits its path on `.` and
		// is falsy-based, so it both mishandles a key containing a period and discards a legitimate 0%
		// override in favour of the statutory figure. The category is a dynamic key — index it directly.
		const override = overrideByCategory[category];
		const thresholdPercent =
			override === null || override === undefined
				? STATUTORY_THRESHOLD_PERCENT_BY_CATEGORY[category]
				: override;
		return {
			value: category,
			label:
				thresholdPercent === null || thresholdPercent === undefined
					? label
					: `${label} (${Number(thresholdPercent)}% leak rate threshold)`,
		};
	});
};

// Same guard as getRefrigerantBandDisplay: nullSafeGetOrElse splits its first argument as a path, so
// an absent category has to be handled before it gets there.
export const getApplianceCategoryLabel = (applianceCategory) =>
	(applianceCategory && APPLIANCE_CATEGORY_LABELS[applianceCategory]) || applianceCategory || '';

/* ── Charge amounts: entered in lb, stored in lb ───────────────────────────── */

/**
 * The forms no longer offer a unit picker — every charge and every event amount is entered in
 * pounds, which is also the only unit the server stores. The conversion below is kept because
 * `toPounds` remains the single place a typed amount is parsed, guarded and rounded to the
 * column's scale, and because a kg entry point (an import, a metric-region rollout) would
 * otherwise reintroduce a rounding constant that has to agree with the server's to the digit.
 */
export const CHARGE_UNITS = { POUND: 'lb', KILOGRAM: 'kg' };

/**
 * Exact by definition (NIST), and the same constant as
 * `utils.RefrigerantLeakRateUtil.PoundToKilogram`. Deliberately NOT the rounded 2.20462 reciprocal:
 * `full_charge_lb` is the leak rate's denominator, so a converted charge that disagrees with the
 * server's own constant would shift every rate computed against it.
 */
const KILOGRAMS_PER_POUND = 0.45359237;

/** `full_charge_lb` and `amount_lb` are DECIMAL(14,5), so 5 dp is exactly what round-trips. */
const CHARGE_SCALE = 5;

const roundToChargeScale = (value) => parseFloat(Number(value).toFixed(CHARGE_SCALE));

/** null (not 0) for a blank or unparseable entry — 0 lb is a real charge and must not be invented. */
export const toPounds = (amount, unit) => {
	if (amount === null || amount === undefined || amount === '') return null;
	const numericAmount = Number(amount);
	if (Number.isNaN(numericAmount)) return null;
	return unit === CHARGE_UNITS.KILOGRAM
		? roundToChargeScale(numericAmount / KILOGRAMS_PER_POUND)
		: roundToChargeScale(numericAmount);
};

/** Inverse of `toPounds`, for showing a stored charge back in the unit the user picked. */
export const fromPounds = (amountLb, unit) => {
	if (amountLb === null || amountLb === undefined || amountLb === '') return null;
	const numericAmount = Number(amountLb);
	if (Number.isNaN(numericAmount)) return null;
	return unit === CHARGE_UNITS.KILOGRAM
		? roundToChargeScale(numericAmount * KILOGRAMS_PER_POUND)
		: roundToChargeScale(numericAmount);
};

/**
 * The refrigerant half of the asset payload, from the asset form's field values.
 *
 * When tracking is OFF this returns `isRefrigerantTracked: false` and NOTHING ELSE — deliberately.
 * The server reads the other three with `optValueString`/`optValueBigDecimal` and falls back to the
 * stored value when a key is absent, so omitting them leaves the recorded charge, type and category
 * intact: they are facts about the equipment, and `isRefrigerantTracked: false` is what removes the
 * unit from the compliance programme. Sending explicit `null`s instead would be actively harmful —
 * `optValueString` turns a JSON null into `Some(None)` rather than `Right(None)`, which corrupts the
 * field rather than clearing it.
 */
export const buildRefrigerantAssetFields = (values) => {
	const isRefrigerantTracked = !!nullSafeGet('isRefrigerantTracked', values);
	if (!isRefrigerantTracked) return { isRefrigerantTracked: false };

	// The form has no unit field — the charge is typed in pounds. toPounds is still what parses,
	// guards and rounds it to the column's 5 dp.
	const fullChargeLb = toPounds(nullSafeGet('fullChargeAmount', values), CHARGE_UNITS.POUND);

	return {
		isRefrigerantTracked: true,
		...(nullSafeGet('applianceCategory', values) && {
			applianceCategory: values.applianceCategory,
		}),
		...(fullChargeLb !== null && { fullChargeLb }),
		...(nullSafeGet('refrigerantTypeCode', values) && {
			refrigerantTypeCode: values.refrigerantTypeCode,
		}),
	};
};

/**
 * The refrigerant half of the ASSET TYPE payload, from the asset template form's field values.
 *
 * Unlike its asset sibling, every key is sent on every save — including the cleared ones as
 * `undefined`. The asset-type PUT is a whole-row replace on the server, so a key omitted from the
 * payload is a value CLEARED in the database; there is no fall-back-to-stored behaviour to lean on.
 * That cuts both ways and is what makes clearing work: unsetting the class's default full charge has
 * to actually unset it, otherwise a type could never be un-configured. `undefined` is used rather than
 * `null` because `optValueString`/`optValueBigDecimal` turn a JSON null into a corrupt `Some(None)`,
 * and `JSON.stringify` drops `undefined` keys entirely — which is exactly the absent key the server
 * reads as "no value".
 *
 * When `usesRefrigerant` is off the three defaults are cleared with it: a class that does not contain
 * refrigerant has no charge or category to default, and leaving stale values behind would hand them to
 * every asset of that type the moment somebody toggled it back on.
 */
export const buildRefrigerantAssetTypeFields = (values) => {
	const usesRefrigerant = !!nullSafeGet('usesRefrigerant', values);
	if (!usesRefrigerant) {
		return {
			usesRefrigerant: false,
			applianceCategory: undefined,
			fullChargeLb: undefined,
			refrigerantTypeCode: undefined,
		};
	}

	// The form has no unit field — the charge is typed in pounds. toPounds is still what parses,
	// guards and rounds it to the column's 5 dp.
	const fullChargeLb = toPounds(nullSafeGet('fullChargeAmount', values), CHARGE_UNITS.POUND);

	return {
		usesRefrigerant: true,
		applianceCategory: nullSafeGet('applianceCategory', values) || undefined,
		fullChargeLb: fullChargeLb === null ? undefined : fullChargeLb,
		refrigerantTypeCode: nullSafeGet('refrigerantTypeCode', values) || undefined,
	};
};

/** The refrigerant picker's display form, matching the design's "R-404A GWP 3922". */
export const formatRefrigerantTypeLabel = (refrigerantType) => {
	const code = nullSafeGet('code', refrigerantType);
	if (!code) return '';
	const gwp = nullSafeGet('gwp', refrigerantType);
	return gwp === null || gwp === undefined ? code : `${code} GWP ${gwp}`;
};

/* ── The effective leak-rate threshold ─────────────────────────────────────── */

/**
 * The threshold the server will band this appliance against, for a screen that only has the
 * appliance category to go on.
 *
 * The asset detail summary (`RefrigerantAssetSummary.thresholdPercent`) is authoritative and must be
 * preferred wherever it is available — this is for the INDEX screen, whose rows come out of
 * OpenSearch and carry `applianceCategory` but no threshold. Mirrors
 * `utils.RefrigerantLeakRateUtil.RefrigerantThresholds.thresholdPercentFor`: a per-buyer override
 * wins, the statutory value is the fallback, and a category with neither (`OtherNonRegulated`)
 * returns null — "log the servicing, do not band it".
 */
export const getThresholdPercentForCategory = (applianceCategory, thresholdOverrides) => {
	if (!applianceCategory) return null;
	const override = (thresholdOverrides || []).find(
		(thresholdOverride) =>
			nullSafeGet('applianceCategory', thresholdOverride) === applianceCategory &&
			nullSafeGetWithZero('leakRateThresholdPercent', thresholdOverride) !== null &&
			nullSafeGetWithZero('leakRateThresholdPercent', thresholdOverride) !== undefined
	);
	if (override) return Number(override.leakRateThresholdPercent);
	const statutory = STATUTORY_THRESHOLD_PERCENT_BY_CATEGORY[applianceCategory];
	return statutory === undefined ? null : statutory;
};

/* ── The ledger's five event types ─────────────────────────────────────────── */

export const REFRIGERANT_EVENT_TYPES = {
	CHARGE: 'Charge',
	/**
	 * Refrigerant taken OUT of the appliance. Covers both a servicing pump-down and an end-of-life
	 * disposal — which one it was is `recoveryReason`, not a second event type.
	 *
	 * `Disposal` used to be its own type and no longer is. The two are the same procedure reporting
	 * the same "quantity recovered" figure into the same recovered total; the only difference is
	 * whether the charge is going back in. As two types, every list that named one had to remember to
	 * name the other (`QUANTITY_BEARING_EVENT_TYPES`, the chip row, the server's
	 * `recoveredLbInWindow`, three clients' label maps), and missing one under-counted a regulated
	 * figure in silence. See `RECOVERY_REASONS`.
	 */
	RECOVER: 'Recover',
	LEAK_REPAIR: 'LeakRepair',
	LEAK_INSPECTION: 'LeakInspection',
	VERIFICATION_TEST: 'VerificationTest',
};

const REFRIGERANT_EVENT_TYPE_LABELS = {
	[REFRIGERANT_EVENT_TYPES.CHARGE]: 'Charge',
	[REFRIGERANT_EVENT_TYPES.RECOVER]: 'Recover',
	[REFRIGERANT_EVENT_TYPES.LEAK_REPAIR]: 'Leak Repair',
	[REFRIGERANT_EVENT_TYPES.LEAK_INSPECTION]: 'Leak Inspection',
	[REFRIGERANT_EVENT_TYPES.VERIFICATION_TEST]: 'Verification Test',
};

/**
 * Drawer chip order and event-log filter order, both taken from the design: the chip row reads as the
 * life of the charge — put in, taken out, repaired, inspected, verified.
 *
 * `REFRIGERANT_EVENT_TYPE_PIPELINE_FILTERS` (below) is generated by mapping over THIS array, and the
 * Event Log's `countBy` is keyed on each chip's `name` — which is the wire value of the type. So
 * editing here is the whole change: the chips and their counts wire themselves up, with no second
 * list to keep in step.
 */
export const REFRIGERANT_EVENT_TYPE_ORDER = [
	REFRIGERANT_EVENT_TYPES.CHARGE,
	REFRIGERANT_EVENT_TYPES.RECOVER,
	REFRIGERANT_EVENT_TYPES.LEAK_REPAIR,
	REFRIGERANT_EVENT_TYPES.LEAK_INSPECTION,
	REFRIGERANT_EVENT_TYPES.VERIFICATION_TEST,
];

export const getRefrigerantEventTypeLabel = (eventType) =>
	(eventType && REFRIGERANT_EVENT_TYPE_LABELS[eventType]) || eventType || '';

/**
 * The types for which `amountLb` is the regulated quantity. Mirrors
 * `models.refrigerant.RefrigerantEventType.quantityBearing` — the server REJECTS any of these with
 * no amount, so the form must require it rather than discover it in a 400.
 *
 * `Recover` covers the disposal case too, whatever its `recoveryReason`: the pounds pulled out of an
 * appliance being retired are its disposal record's "quantity recovered" figure and they roll into
 * the same `recoveredLbTrailing12Mo` total an ordinary recovery does. Keeping them in one type is
 * what stops the recovered figure on the compliance screen understating what left the unit.
 */
export const QUANTITY_BEARING_EVENT_TYPES = [
	REFRIGERANT_EVENT_TYPES.CHARGE,
	REFRIGERANT_EVENT_TYPES.RECOVER,
];

/**
 * The types that record an observation rather than work done. Mirrors
 * `models.refrigerant.RefrigerantEventResult.resultBearing`; `result` is required for both, for the
 * same reason `amountLb` is required above — an inspection with no result advances the next
 * inspection date while recording nothing about whether a leak was found.
 *
 * `Charge` and `Recover` are quantity-bearing and deliberately NOT result-bearing: they move a
 * measured quantity and record no pass/fail observation about the appliance.
 */
export const RESULT_BEARING_EVENT_TYPES = [
	REFRIGERANT_EVENT_TYPES.LEAK_INSPECTION,
	REFRIGERANT_EVENT_TYPES.VERIFICATION_TEST,
];

export const REFRIGERANT_EVENT_RESULTS = { PASS: 'Pass', FAIL: 'Fail' };

export const VERIFICATION_STAGES = { INITIAL: 'Initial', FOLLOW_UP: 'FollowUp' };

export const VERIFICATION_STAGE_OPTIONS = [
	{ value: VERIFICATION_STAGES.INITIAL, label: 'Initial' },
	// Two verification tests are required after a repair — the initial one and this follow-up.
	{ value: VERIFICATION_STAGES.FOLLOW_UP, label: 'Follow-up' },
];

/**
 * The stage's display label, and the ONE place it is spelled out.
 *
 * It existed in both mobile apps' utils and not here, so the web app inlined the mapping in
 * `RefrigerantEventTypeDisplay` and the CSV export — which reads the same events — carried no stage at
 * all. On the printed sheet an initial pass and a follow-up pass were distinguishable; in the file an
 * auditor is handed they were not, which is exactly the failure the stage exists to prevent.
 *
 * Returns '' for an absent stage rather than a placeholder: only verification tests have stages, so every
 * other row would otherwise gain a meaningless cell.
 */
export const getVerificationStageLabel = (verificationStage) => {
	const option = VERIFICATION_STAGE_OPTIONS.find(({ value }) => value === verificationStage);
	return option ? option.label : '';
};

export const isQuantityBearingEventType = (eventType) =>
	QUANTITY_BEARING_EVENT_TYPES.indexOf(eventType) !== -1;

export const isResultBearingEventType = (eventType) =>
	RESULT_BEARING_EVENT_TYPES.indexOf(eventType) !== -1;

/**
 * Where the leak was found. Optional even on a repair, deliberately: it is typed on a ladder in a
 * mechanical room, and refusing the write over it would lose the whole event rather than one field.
 */
export const shouldCaptureLeakSource = (eventType, result) =>
	eventType === REFRIGERANT_EVENT_TYPES.LEAK_REPAIR ||
	(isResultBearingEventType(eventType) && result === REFRIGERANT_EVENT_RESULTS.FAIL);

/* ── Why the refrigerant went in ───────────────────────────────────────────── */

/**
 * Why a Charge was made. Mirrors `models.refrigerant.RefrigerantChargeReason`.
 *
 * This exists because the leak rate's numerator is "refrigerant added to replace what leaked out",
 * and until now every charge was treated as exactly that. Commissioning a new unit, retrofitting it
 * to a different refrigerant, or topping up a seasonal swing all add pounds without any leak having
 * occurred — and counting them inflated a REGULATED figure and could band a healthy appliance as
 * Exceeded.
 *
 * NOT named `CHARGE_TYPES`: `CHARGE_UNITS` already lives in this file and the two would read as a
 * pair when they have nothing to do with each other.
 */
export const CHARGE_REASONS = {
	TOP_OFF: 'TopOff',
	NEW_INSTALL: 'NewInstall',
	RETROFIT: 'Retrofit',
	SEASONAL_VARIANCE: 'SeasonalVariance',
};

const CHARGE_REASON_LABELS = {
	[CHARGE_REASONS.TOP_OFF]: 'Top Off',
	[CHARGE_REASONS.NEW_INSTALL]: 'New Install',
	[CHARGE_REASONS.RETROFIT]: 'Retrofit',
	[CHARGE_REASONS.SEASONAL_VARIANCE]: 'Seasonal Variance',
};

/** The drawer's pill order, modelled on `VERIFICATION_STAGE_OPTIONS`. Top-off first: it is both the
 *  commonest reason and the only one that moves the regulated rate. */
export const CHARGE_REASON_OPTIONS = [
	{ value: CHARGE_REASONS.TOP_OFF, label: CHARGE_REASON_LABELS[CHARGE_REASONS.TOP_OFF] },
	{ value: CHARGE_REASONS.NEW_INSTALL, label: CHARGE_REASON_LABELS[CHARGE_REASONS.NEW_INSTALL] },
	{ value: CHARGE_REASONS.RETROFIT, label: CHARGE_REASON_LABELS[CHARGE_REASONS.RETROFIT] },
	{
		value: CHARGE_REASONS.SEASONAL_VARIANCE,
		label: CHARGE_REASON_LABELS[CHARGE_REASONS.SEASONAL_VARIANCE],
	},
];

/** Same guard as `getApplianceCategoryLabel` — an unknown or absent reason falls back to the raw
 *  wire value rather than rendering `undefined` into a compliance document. */
export const getChargeReasonLabel = (chargeReason) =>
	(chargeReason && CHARGE_REASON_LABELS[chargeReason]) || chargeReason || '';

/**
 * The reasons whose pounds enter the trailing-12-month leak-rate numerator. Mirrors
 * `models.refrigerant.RefrigerantChargeReason.leakRateBearing` exactly — one entry, and it must stay
 * one entry on both sides: a reason the server counts and the drawer does not would make the
 * projected rate disagree with the rate the ledger then reports.
 */
export const LEAK_RATE_BEARING_CHARGE_REASONS = [CHARGE_REASONS.TOP_OFF];

/**
 * Whether a charge with this reason moves the leak rate. Mirrors
 * `models.refrigerant.RefrigerantChargeReason.countsTowardLeakRate`, including the part that looks
 * like a bug and is not: a MISSING reason COUNTS.
 *
 * Every charge logged before this field existed carries no reason, and those pounds are already in
 * the leak rates and compliance bands on record. Treating a null as "not a leak" would silently zero
 * out a regulated number across the whole existing ledger — a filed 608 figure would change with no
 * event having been logged. So absent means "counts", exactly as it always did, and only an
 * explicit non-top-off reason takes a charge out of the rate.
 *
 * The same rule governs CO2e: it is derived from the very same pounds and is printed beside the
 * added-pounds figure on one screen, so a reason that skipped one and not the other would render
 * "0 lb added / 40 t CO2e".
 */
export const doesChargeReasonCountTowardLeakRate = (chargeReason) =>
	!chargeReason || LEAK_RATE_BEARING_CHARGE_REASONS.indexOf(chargeReason) !== -1;

/**
 * Whether the form should ask for a reason at all. Modelled on `shouldCaptureLeakSource`, but unlike
 * the leak source this one is REQUIRED where it applies — the server rejects a Charge without it,
 * because the reason is what decides whether the pounds enter a regulated figure, and defaulting it
 * would be the client silently deciding that on the tech's behalf.
 *
 * Meaningless on every other type: nothing is being added, so there is no reason for adding it.
 */
export const shouldCaptureChargeReason = (eventType) =>
	eventType === REFRIGERANT_EVENT_TYPES.CHARGE;

/* ── Why the refrigerant came out ──────────────────────────────────────────── */

/**
 * Why a Recover was made. Mirrors `models.refrigerant.RefrigerantRecoveryReason`.
 *
 * This is what replaced the standalone `Disposal` event type. Both are the same procedure pulling the
 * same measured quantity out of the machine and reporting it into the same recovered total; what
 * differs is whether that charge is going back in. Expressed as two types, the difference had to be
 * restated in every list that named a type, and a list that named only one of them under-counted a
 * regulated figure with nothing to notice. Expressed as a reason, there is one type to select and one
 * field to read.
 *
 * `DISPOSAL` is the statement that the refrigerant went for reclaim or destruction — the thing the EPA
 * disposal record has to be able to point at, and what feeds
 * `RefrigerantAssetSummary.lastDisposalAt`. It is still NOT terminal for the appliance: logging one
 * closes no inspection or verification obligation, because retiring the unit is a separate act on the
 * asset itself.
 */
export const RECOVERY_REASONS = {
	RECOVER: 'Recover',
	DISPOSAL: 'Disposal',
};

const RECOVERY_REASON_LABELS = {
	[RECOVERY_REASONS.RECOVER]: 'Recover',
	[RECOVERY_REASONS.DISPOSAL]: 'Disposal',
};

/** Recovery first, and it is also the drawer's default: pumping a charge down to open a system is
 *  what the overwhelming majority of recoveries are, and a disposal is the exception worth stating. */
export const RECOVERY_REASON_OPTIONS = [
	{ value: RECOVERY_REASONS.RECOVER, label: RECOVERY_REASON_LABELS[RECOVERY_REASONS.RECOVER] },
	{ value: RECOVERY_REASONS.DISPOSAL, label: RECOVERY_REASON_LABELS[RECOVERY_REASONS.DISPOSAL] },
];

/** Same guard as `getChargeReasonLabel` — an unknown or absent reason falls back to the raw wire
 *  value rather than rendering `undefined` into a compliance document. */
export const getRecoveryReasonLabel = (recoveryReason) =>
	(recoveryReason && RECOVERY_REASON_LABELS[recoveryReason]) || recoveryReason || '';

/**
 * Whether the form should ask why refrigerant came out. The mirror of `shouldCaptureChargeReason`,
 * and REQUIRED where it applies for the same class of reason: the server rejects a `Recover` without
 * it, because this field is the only thing on the row that says whether the appliance was serviced or
 * emptied for good.
 *
 * Meaningless on every other type: nothing else removes refrigerant.
 */
export const shouldCaptureRecoveryReason = (eventType) =>
	eventType === REFRIGERANT_EVENT_TYPES.RECOVER;

/* ── Technician EPA 608 certification ──────────────────────────────────────── */

/**
 * The 608 technician certification classes. Mirrors `models.refrigerant.EpaCertificationType`.
 *
 * The values carry their own spaces and roman numerals ('Type II') because that is the designation
 * as the EPA writes it and as it is printed on the card the tech holds — a 608 record that rendered
 * `TYPE_II` would not match the document it attests to.
 */
export const EPA_CERTIFICATION_TYPES = {
	TYPE_I: 'Type I',
	TYPE_II: 'Type II',
	TYPE_III: 'Type III',
	UNIVERSAL: 'Universal',
};

/**
 * Keyed by the WIRE VALUE, like every other label map here, so the lookup in
 * `getEpaCertificationTypeLabel` is a real one. Each label happens to equal its value because the
 * designation is already the way the EPA prints it — the map exists so a future gloss ("Type II
 * (high-pressure)") has somewhere to go that cannot desynchronise from the value on the wire.
 */
const EPA_CERTIFICATION_TYPE_LABELS = {
	[EPA_CERTIFICATION_TYPES.TYPE_I]: 'Type I',
	[EPA_CERTIFICATION_TYPES.TYPE_II]: 'Type II',
	[EPA_CERTIFICATION_TYPES.TYPE_III]: 'Type III',
	[EPA_CERTIFICATION_TYPES.UNIVERSAL]: 'Universal',
};

/** Ordered as the EPA lists them — small appliances, high-pressure, low-pressure, then the
 *  Universal certification that covers all three. */
export const EPA_CERTIFICATION_TYPE_OPTIONS = [
	{
		value: EPA_CERTIFICATION_TYPES.TYPE_I,
		label: EPA_CERTIFICATION_TYPE_LABELS[EPA_CERTIFICATION_TYPES.TYPE_I],
	},
	{
		value: EPA_CERTIFICATION_TYPES.TYPE_II,
		label: EPA_CERTIFICATION_TYPE_LABELS[EPA_CERTIFICATION_TYPES.TYPE_II],
	},
	{
		value: EPA_CERTIFICATION_TYPES.TYPE_III,
		label: EPA_CERTIFICATION_TYPE_LABELS[EPA_CERTIFICATION_TYPES.TYPE_III],
	},
	{
		value: EPA_CERTIFICATION_TYPES.UNIVERSAL,
		label: EPA_CERTIFICATION_TYPE_LABELS[EPA_CERTIFICATION_TYPES.UNIVERSAL],
	},
];

/**
 * Same guard as `getApplianceCategoryLabel`: an unrecognised or legacy designation renders as
 * ITSELF rather than blank. A certification number is worthless without the class beside it, and
 * blanking an unknown class on a 608 record would quietly drop half the attestation.
 */
export const getEpaCertificationTypeLabel = (certificationType) =>
	(certificationType && EPA_CERTIFICATION_TYPE_LABELS[certificationType]) ||
	certificationType ||
	'';

/**
 * Whether this contact's own record already carries a 608 certification — rule (a) of the server's
 * acceptance test in `RefrigerantEventServiceImpl`: an event is accepted iff EITHER the named
 * technician's contact carries BOTH cert fields, OR the event itself supplies technician name, cert
 * type and cert number.
 *
 * BOTH halves are required, not either: a type with no number names a class of certification rather
 * than a certified person, and a number with no type cannot be checked against anything. Blank
 * strings are treated as absent — an empty text field saved on the contact form is not a
 * certification.
 *
 * Mirrored here so the drawer can decide which fields to require BEFORE the write, rather than
 * showing the tech a 400. The server still enforces it; this never relaxes it.
 */
export const hasTechnicianCertification = (contact) => {
	const certificationType = nullSafeGet('epaCertificationType', contact);
	const certificationNumber = nullSafeGet('epaCertificationNumber', contact);
	return `${certificationType || ''}`.trim() !== '' && `${certificationNumber || ''}`.trim() !== '';
};

/**
 * `Type II · 5510-114` from a CONTACT's stored credential — the technician picker's option subtitle
 * and the Field Tech card's cert row.
 *
 * Deliberately distinct from `RefrigerantEventCells.formatEventCertification`, which reads the same
 * two field names off an EVENT. The similarity is the point of the separation rather than an argument
 * for merging them: the event's pair is a frozen snapshot of what was attested to, the contact's is
 * what the person holds today, and a helper that took either would let a screen print one where it
 * meant the other. On a ledger row that is the difference between a record and a guess.
 *
 * `''` when neither half is present, so a caller can render nothing without testing two fields; a
 * half-filled credential still renders its half, because "Type II, no number" is exactly the state
 * somebody needs to see in order to fix it.
 */
export const formatContactEpaCertification = (contact) =>
	[
		getEpaCertificationTypeLabel(nullSafeGet('epaCertificationType', contact)),
		nullSafeGet('epaCertificationNumber', contact),
	]
		.filter(Boolean)
		.join(' · ');

/**
 * Where the technician on a 608 event comes from — an internal tech with a contact record here, or an
 * outside contractor who does not have one.
 *
 * This is a CLIENT-SIDE distinction with no wire field behind it, and that is deliberate. The server
 * accepts an event two ways (`technicianEmail` naming a certified contact, or the manual
 * name/type/number triple), and which one a payload uses is already visible in the payload itself —
 * adding a column saying which branch the form was on would be a third source of truth about the same
 * fact. What this drives is only which fields the form shows.
 *
 * `INTERNAL` is the default because the picker is the path that produces the better record: the
 * server snapshots the contact's own credential and display name onto the row, so nothing depends on
 * what somebody typed.
 */
export const TECHNICIAN_SOURCES = {
	INTERNAL: 'internal',
	EXTERNAL: 'external',
};

export const TECHNICIAN_SOURCE_OPTIONS = [
	{ value: TECHNICIAN_SOURCES.INTERNAL, label: 'Internal' },
	{ value: TECHNICIAN_SOURCES.EXTERNAL, label: 'External' },
];

/* ── Compliance bands ──────────────────────────────────────────────────────── */

export const REFRIGERANT_COMPLIANCE_BANDS = {
	COMPLIANT: 'Compliant',
	WATCH: 'Watch',
	EXCEEDED: 'Exceeded',
};

/**
 * Pill colours, in the repo's `*StatusDisplay` shape (text colour + background). Green / amber / red
 * follow `STOPLIGHT_COLORS`, so a band reads the same way as every other status in the product.
 */
const REFRIGERANT_BAND_DISPLAY = {
	[REFRIGERANT_COMPLIANCE_BANDS.COMPLIANT]: {
		label: 'Compliant',
		color: 'rgb(0, 99, 6)',
		backgroundColor: '#e1fbe7',
		barColor: '#52c41a',
	},
	[REFRIGERANT_COMPLIANCE_BANDS.WATCH]: {
		label: 'Watch',
		color: 'rgb(160, 118, 0)',
		backgroundColor: '#fff4d6',
		barColor: '#faad14',
	},
	[REFRIGERANT_COMPLIANCE_BANDS.EXCEEDED]: {
		label: 'Threshold Exceeded',
		color: 'rgb(163, 40, 40)',
		backgroundColor: '#f9e6e7',
		barColor: '#ff4d4f',
	},
};

/**
 * The band is absent for two distinct reasons — the appliance is under the 50 lb scope floor, or its
 * category is positively out of scope — and the index screen cannot tell them apart (OPEN-4 in the
 * design). Either way the honest label is "not banded", never "Compliant": an appliance no rule
 * covers has no conclusion to report, and Compliant is a conclusion.
 */
export const NOT_BANDED_DISPLAY = {
	label: 'Not banded',
	color: 'rgba(0,0,0,0.65)',
	backgroundColor: '#f2f2f2',
	barColor: '#d9d9d9',
};

/**
 * Guarded on `band` being present rather than routed through `nullSafeGetOrElse`: that helper takes
 * the key as a dot PATH and calls `.split` on it, so an absent band — the common case, since every
 * appliance under the scope floor has one — would throw rather than fall back.
 */
export const getRefrigerantBandDisplay = (band) =>
	(band && REFRIGERANT_BAND_DISPLAY[band]) || NOT_BANDED_DISPLAY;

/**
 * The band as a sentence-case status for the printed servicing record — "Threshold exceeded".
 *
 * A separate label from the pill's, because the record is prose rather than UI chrome and reads in a
 * "Status:" column. It still routes through the same map, so a band can never be described one way
 * on screen and another on paper.
 */
export const getRefrigerantComplianceStatusLabel = (band) => {
	const { label } = getRefrigerantBandDisplay(band);
	return label.charAt(0) + label.slice(1).toLowerCase();
};

/* ── Compliance programmes ─────────────────────────────────────────────────── */

/**
 * The full charge below which an appliance is not banded at all. Mirrors
 * `utils.RefrigerantLeakRateUtil.BandingScopeFloorLb`, and it is the same 50 lb every programme's
 * scope line quotes.
 */
export const BANDING_SCOPE_FLOOR_LB = 50;

export const REFRIGERANT_PROGRAMMES = {
	EPA_608: 'epa608',
	CARB_RMP: 'carbRmp',
	GREEN_CHILL: 'greenChill',
};

/**
 * Short category names for a programme's standards line — "Industrial process refrigeration", not
 * `APPLIANCE_CATEGORY_LABELS`' "Industrial Process Refrigeration (IPR)". The banner is a sentence
 * about a programme, and the (IPR) gloss belongs in the picker where the user selects a category.
 */
const PROGRAMME_CATEGORY_LABELS = {
	[APPLIANCE_CATEGORIES.COMMERCIAL_REFRIGERATION]: 'Commercial refrigeration',
	[APPLIANCE_CATEGORIES.INDUSTRIAL_PROCESS]: 'Industrial process refrigeration',
	[APPLIANCE_CATEGORIES.COMFORT_COOLING]: 'Comfort cooling',
};

/** The order the standards line lists categories in, per the design. */
const PROGRAMME_CATEGORY_ORDER = [
	APPLIANCE_CATEGORIES.COMMERCIAL_REFRIGERATION,
	APPLIANCE_CATEGORIES.INDUSTRIAL_PROCESS,
	APPLIANCE_CATEGORIES.COMFORT_COOLING,
];

/**
 * The three refrigerant compliance programmes, each with its own scope and leak-rate thresholds.
 *
 * `OtherNonRegulated` is absent from every threshold table, exactly as it is absent from
 * `STATUTORY_THRESHOLD_PERCENT_BY_CATEGORY` — it means "log the servicing, do not band it", and a 0%
 * entry would band everything in it as exceeded.
 *
 * `isServerBanded` marks the ONE programme the server computes: EPA 608. Its threshold and band come
 * off `RefrigerantAssetSummary` rather than this table, because that is what the server actually
 * banded the appliance against — including any per-buyer override — and a record that recomputed it
 * here could contradict the appliance's own screen. The other two are voluntary or state programmes
 * the server has no notion of, so their status is derived on read against their own numbers, and a
 * buyer's 608 override deliberately does NOT move them: an override is that buyer's stricter internal
 * target for 608, not a restatement of California's rule or of GreenChill's certification target.
 */
const REFRIGERANT_PROGRAMME_DEFINITIONS = {
	[REFRIGERANT_PROGRAMMES.EPA_608]: {
		key: REFRIGERANT_PROGRAMMES.EPA_608,
		tabLabel: 'EPA 608',
		name: 'EPA Section 608, subpart F + AIM Act',
		scope: 'Assets 50 lb and above are leak-rate regulated; 15 lb and above must be repaired',
		thresholdPercentByCategory: {
			[APPLIANCE_CATEGORIES.COMMERCIAL_REFRIGERATION]: 20,
			[APPLIANCE_CATEGORIES.INDUSTRIAL_PROCESS]: 30,
			[APPLIANCE_CATEGORIES.COMFORT_COOLING]: 10,
		},
		isServerBanded: true,
		thresholdLabel: 'Threshold',
		footer:
			'Certified under EPA Section 608, subpart F + AIM Act. Technician EPA certification numbers are recorded on every event, and recovery for disposal is documented against the disposal event. This record is generated from the work-order ledger — no separate data entry.',
	},
	[REFRIGERANT_PROGRAMMES.CARB_RMP]: {
		key: REFRIGERANT_PROGRAMMES.CARB_RMP,
		tabLabel: 'CARB RMP',
		name: 'California Refrigerant Management Program',
		scope: 'All systems 50 lb and above, annual registration and inspection reporting',
		thresholdPercentByCategory: {
			[APPLIANCE_CATEGORIES.COMMERCIAL_REFRIGERATION]: 20,
			[APPLIANCE_CATEGORIES.INDUSTRIAL_PROCESS]: 30,
			[APPLIANCE_CATEGORIES.COMFORT_COOLING]: 10,
		},
		isServerBanded: false,
		thresholdLabel: 'Threshold',
		footer:
			'California Refrigerant Management Program. Systems 50 lb and above require annual registration and inspection reporting; the service events below are the inspection record for this system. This record is generated from the work-order ledger — no separate data entry.',
	},
	[REFRIGERANT_PROGRAMMES.GREEN_CHILL]: {
		key: REFRIGERANT_PROGRAMMES.GREEN_CHILL,
		tabLabel: 'GreenChill',
		name: 'EPA GreenChill Partnership (voluntary)',
		scope: 'Store certification target — corporate leak rate reported annually',
		thresholdPercentByCategory: {
			[APPLIANCE_CATEGORIES.COMMERCIAL_REFRIGERATION]: 15,
			[APPLIANCE_CATEGORIES.INDUSTRIAL_PROCESS]: 15,
			[APPLIANCE_CATEGORIES.COMFORT_COOLING]: 15,
		},
		isServerBanded: false,
		// "Target", not "Threshold": GreenChill is voluntary and certification is assessed on the
		// CORPORATE rate. Calling this appliance's 15% a threshold would imply a per-unit rule exists.
		thresholdLabel: 'Target',
		footer:
			'EPA GreenChill Partnership (voluntary). Store certification is assessed on the corporate leak rate, reported annually; this appliance contributes to that figure rather than being certified on its own. This record is generated from the work-order ledger — no separate data entry.',
	},
};

export const REFRIGERANT_PROGRAMME_ORDER = [
	REFRIGERANT_PROGRAMMES.EPA_608,
	REFRIGERANT_PROGRAMMES.CARB_RMP,
	REFRIGERANT_PROGRAMMES.GREEN_CHILL,
];

/** Comparison form for a free-text address field: lowercase, letters and digits only. */
const normalizeAddressValue = (value) =>
	typeof value === 'string' ? value.toLowerCase().replace(/[^a-z0-9]/g, '') : '';

/**
 * The state is free text — typed by hand on the location form, or written by the Google Places
 * autocomplete, which stores the `long_name` ("California"). Both spellings therefore occur in real
 * data, and an import can carry either.
 */
const CALIFORNIA_REGION_VALUES = ['ca', 'calif', 'california'];

/**
 * The country, normalized. Blank counts as a match: a US state name with no country beside it is not
 * evidence of a different country, and plenty of rows carry the state alone.
 */
const UNITED_STATES_COUNTRY_VALUES = ['', 'us', 'usa', 'unitedstates', 'unitedstatesofamerica'];

/**
 * Whether an address is in California, USA — the jurisdiction CARB's Refrigerant Management Program
 * actually covers.
 *
 * The country is checked as well as the state so a two-letter `CA` written against another country
 * cannot be read as California; a blank country still passes, because the overwhelming majority of a
 * US tenant's rows carry a state and nothing else.
 */
export const isCaliforniaAddress = (address) =>
	CALIFORNIA_REGION_VALUES.indexOf(normalizeAddressValue(nullSafeGet('region', address))) > -1 &&
	UNITED_STATES_COUNTRY_VALUES.indexOf(normalizeAddressValue(nullSafeGet('country', address))) > -1;

/**
 * The appliance's site address.
 *
 * Two places carry it because two hydrations do, and they describe the same site: a location's
 * street address IS its buyer facility's `primaryAddress` — `LocationForm` creates one facility per
 * location, named after it and carrying the address typed into it, which is why the location detail
 * page renders that field as the store's address.
 *
 * The LOCATION's facility is read first, deliberately. The asset's own `buyerFacilityId` resolves to
 * the same facility — the server overwrites whatever a client sends with `location.buyerFacilityId`
 * when it builds the row (`AssetCommonMethods.constructEntityObjectFuture`), which is what makes the
 * asset branch safe — but the asset FORM seeds that field from the current user's own facility
 * (`AssetForm`), so a reader checking this code has to know about a server-side override to satisfy
 * themselves it cannot resolve to whoever created the appliance. Taking the location first removes
 * that reasoning step: the branch that is unambiguously the site's answers first, and the asset's is
 * the fallback for a payload hydrated the other way round. It also matches how every other consumer
 * in this codebase reads `buyerFacility.primaryAddress` — off a location.
 */
export const getAssetSiteAddress = (asset) =>
	nullSafeGet('location.buyerFacility.primaryAddress', asset) ||
	nullSafeGet('buyerFacility.primaryAddress', asset) ||
	null;

/**
 * The programmes whose record may be produced for THIS appliance, in tab order.
 *
 * CARB RMP is a California state programme: it is the California Air Resources Board's rule, and an
 * appliance in Ohio is not registered under it and never reports to it. Offering the tab there
 * invited a reader to generate a document asserting compliance with a programme the appliance is not
 * in — which on a compliance record is a false statement, not a spare option.
 *
 * Withheld when the site's state is UNKNOWN as well as when it is elsewhere, deliberately. The two
 * are different facts, but the safe answer is the same for both: a CARB record is only defensible on
 * an appliance positively known to sit in California, so an address that has not been filled in is a
 * reason not to offer the document rather than a reason to assume it applies.
 *
 * EPA 608 is federal and GreenChill is a voluntary national partnership, so neither is geographic.
 */
export const getRefrigerantProgrammeKeysForAsset = (asset) =>
	REFRIGERANT_PROGRAMME_ORDER.filter(
		(programmeKey) =>
			programmeKey !== REFRIGERANT_PROGRAMMES.CARB_RMP ||
			isCaliforniaAddress(getAssetSiteAddress(asset))
	);

/** Falls back to EPA 608 — the statutory programme — rather than returning undefined. */
export const getRefrigerantProgramme = (programmeKey) =>
	REFRIGERANT_PROGRAMME_DEFINITIONS[programmeKey] ||
	REFRIGERANT_PROGRAMME_DEFINITIONS[REFRIGERANT_PROGRAMMES.EPA_608];

/**
 * The programme's standards line, as the design prints it:
 * `<name> · <scope> · thresholds Commercial refrigeration 20% · …`
 *
 * Generated from the definition rather than stored as prose so the sentence and the numbers the
 * record actually bands against can never disagree.
 */
export const getRefrigerantProgrammeStandardsLine = (programmeKey) => {
	const programme = getRefrigerantProgramme(programmeKey);
	const thresholds = PROGRAMME_CATEGORY_ORDER.map(
		(category) =>
			`${PROGRAMME_CATEGORY_LABELS[category]} ${programme.thresholdPercentByCategory[category]}%`
	).join(' · ');
	return `${programme.name} · ${programme.scope} · thresholds ${thresholds}`;
};

/**
 * The threshold this appliance is measured against under `programmeKey`.
 *
 * For EPA 608 the server's own `thresholdPercent` wins whenever it is present: it already resolved
 * the per-buyer override, and the table here is only the statutory fallback for a summary that
 * carries none.
 */
export const getProgrammeThresholdPercent = (programmeKey, applianceCategory, summary) => {
	const programme = getRefrigerantProgramme(programmeKey);
	if (programme.isServerBanded) {
		const serverThreshold = nullSafeGetWithZero('thresholdPercent', summary);
		if (serverThreshold !== null && serverThreshold !== undefined) return Number(serverThreshold);
	}
	if (!applianceCategory) return null;
	const threshold = programme.thresholdPercentByCategory[applianceCategory];
	return threshold === undefined ? null : threshold;
};

/**
 * `Compliant` below half the threshold, `Watch` below it, `Exceeded` at or above — and `null` below
 * the 50 lb banding floor. Mirrors `utils.RefrigerantLeakRateUtil.band` exactly.
 *
 * Client-side ONLY for the programmes the server does not compute. EPA 608 must always come off the
 * summary; see `getProgrammeComplianceBand`.
 */
export const computeComplianceBand = (leakRatePercent, thresholdPercent, fullChargeLb) => {
	const rate = Number(leakRatePercent);
	const threshold = Number(thresholdPercent);
	const charge = Number(fullChargeLb);
	if (
		leakRatePercent === null ||
		leakRatePercent === undefined ||
		thresholdPercent === null ||
		thresholdPercent === undefined ||
		fullChargeLb === null ||
		fullChargeLb === undefined ||
		Number.isNaN(rate) ||
		Number.isNaN(threshold) ||
		Number.isNaN(charge)
	) {
		return null;
	}
	if (charge < BANDING_SCOPE_FLOOR_LB) return null;
	if (rate >= threshold) return REFRIGERANT_COMPLIANCE_BANDS.EXCEEDED;
	if (rate >= threshold / 2) return REFRIGERANT_COMPLIANCE_BANDS.WATCH;
	return REFRIGERANT_COMPLIANCE_BANDS.COMPLIANT;
};

/** The band to print for a programme — the server's for EPA 608, derived for the other two. */
export const getProgrammeComplianceBand = (programmeKey, summary, thresholdPercent) => {
	const programme = getRefrigerantProgramme(programmeKey);
	if (programme.isServerBanded) return nullSafeGet('complianceBand', summary) || null;
	return computeComplianceBand(
		nullSafeGetWithZero('leakRatePercent', summary),
		thresholdPercent,
		nullSafeGetWithZero('fullChargeLb', summary)
	);
};

/* ── The index screen's buckets ────────────────────────────────────────────── */

/**
 * The five server-side buckets plus `inactive`.
 *
 * They OVERLAP by design and that is not an oversight — the bands partition the active tracked set,
 * `inactive` is a separate partition, and the two due buckets are overlays that intersect the bands.
 * Selecting a band and an overlay together therefore narrows to the intersection, which is what the
 * design intends ("Rooftop Chiller 1 carries both a Threshold exceeded chip and a Verification test
 * due link").
 *
 * `inactive` has no server bucket — the counts endpoint returns the other five — so its count is
 * fetched separately. See `refreshRefrigerantBandCounts`.
 */
export const REFRIGERANT_BUCKETS = {
	COMPLIANT: 'compliant',
	WATCH: 'watch',
	EXCEEDED: 'exceeded',
	INSPECTION_DUE: 'inspectionDue',
	VERIFICATION_DUE: 'verificationDue',
	INACTIVE: 'inactive',
};

/** The five the server aggregates in one request. `inactive` is deliberately not one of them. */
export const REFRIGERANT_SERVER_BUCKET_NAMES = [
	REFRIGERANT_BUCKETS.COMPLIANT,
	REFRIGERANT_BUCKETS.WATCH,
	REFRIGERANT_BUCKETS.EXCEEDED,
	REFRIGERANT_BUCKETS.INSPECTION_DUE,
	REFRIGERANT_BUCKETS.VERIFICATION_DUE,
];

const BUCKET_TO_BAND = {
	[REFRIGERANT_BUCKETS.COMPLIANT]: REFRIGERANT_COMPLIANCE_BANDS.COMPLIANT,
	[REFRIGERANT_BUCKETS.WATCH]: REFRIGERANT_COMPLIANCE_BANDS.WATCH,
	[REFRIGERANT_BUCKETS.EXCEEDED]: REFRIGERANT_COMPLIANCE_BANDS.EXCEEDED,
};

/**
 * The single filter key the bucket chips write into.
 *
 * It is a FRONTEND-ONLY key: the server has no `refrigerantBuckets` filter, and
 * `AssetsFilterUtil`/`AssetOpenSearchHelper` silently ignore keys they do not recognise, so it MUST
 * be translated away before the request leaves — see `toRefrigerantApiFilters`. Keeping the chips on
 * one key is what lets the shared `PipelineFilter` drive them.
 */
export const REFRIGERANT_BUCKETS_FILTER_KEY = 'refrigerantBuckets';

/**
 * The Assets sub-tab's chips. `filter` is unused by `PipelineFilter` (it reads `name`) but is kept
 * because every other pipeline-filter definition in the repo carries it.
 */
export const REFRIGERANT_BUCKET_PIPELINE_FILTERS = [
	{
		displayName: 'Compliant',
		name: REFRIGERANT_BUCKETS.COMPLIANT,
		filter: { [REFRIGERANT_BUCKETS_FILTER_KEY]: REFRIGERANT_BUCKETS.COMPLIANT },
		icon: 'icons8-checkmark',
		iconStyle: { color: REFRIGERANT_BAND_DISPLAY[REFRIGERANT_COMPLIANCE_BANDS.COMPLIANT].barColor },
	},
	{
		displayName: 'Watch',
		name: REFRIGERANT_BUCKETS.WATCH,
		filter: { [REFRIGERANT_BUCKETS_FILTER_KEY]: REFRIGERANT_BUCKETS.WATCH },
		icon: 'icons8-warning',
		iconStyle: { color: REFRIGERANT_BAND_DISPLAY[REFRIGERANT_COMPLIANCE_BANDS.WATCH].barColor },
	},
	{
		displayName: 'Threshold Exceeded',
		name: REFRIGERANT_BUCKETS.EXCEEDED,
		filter: { [REFRIGERANT_BUCKETS_FILTER_KEY]: REFRIGERANT_BUCKETS.EXCEEDED },
		icon: 'icons8-error',
		iconStyle: { color: REFRIGERANT_BAND_DISPLAY[REFRIGERANT_COMPLIANCE_BANDS.EXCEEDED].barColor },
	},
	{
		displayName: 'Inspection due',
		name: REFRIGERANT_BUCKETS.INSPECTION_DUE,
		filter: { [REFRIGERANT_BUCKETS_FILTER_KEY]: REFRIGERANT_BUCKETS.INSPECTION_DUE },
		icon: 'icons8-search',
		iconStyle: { color: 'rgb(11, 28, 111)' },
	},
	{
		displayName: 'Verification due',
		name: REFRIGERANT_BUCKETS.VERIFICATION_DUE,
		filter: { [REFRIGERANT_BUCKETS_FILTER_KEY]: REFRIGERANT_BUCKETS.VERIFICATION_DUE },
		icon: 'icons8-time',
		iconStyle: { color: 'rgb(213, 125, 5)' },
	},
	{
		displayName: 'Inactive',
		name: REFRIGERANT_BUCKETS.INACTIVE,
		filter: { [REFRIGERANT_BUCKETS_FILTER_KEY]: REFRIGERANT_BUCKETS.INACTIVE },
		icon: 'icons8-cancel',
		iconStyle: { color: 'rgba(0,0,0,0.45)' },
	},
];

/** Every filter key `toRefrigerantApiFilters` may emit, so a caller can clear them all cleanly. */
const REFRIGERANT_DERIVED_FILTER_KEYS = [
	'refrigerantComplianceBands',
	'refrigerantInspectionDueBefore',
	'refrigerantVerificationDueBefore',
	'isActive',
];

/**
 * Turns the screen's own `refrigerantBuckets` selection into the filter keys the asset search
 * actually understands, and pins the list to tracked appliances.
 *
 * `nowMillis` is passed in rather than read here so the value that decides "due" is the same one for
 * the list and for the counts within a single render, and so it is testable.
 *
 * The two due buckets become `lte now` bounds — this is the house pattern for the projection: a
 * time-invariant scalar is stored, and `now` enters as a query bound computed by the caller.
 */
export const toRefrigerantApiFilters = (filters, nowMillis) => {
	const { [REFRIGERANT_BUCKETS_FILTER_KEY]: rawBuckets, ...rest } = filters || ({} as any);

	const buckets = `${rawBuckets || ''}`.split(',').filter((bucket) => !!bucket);
	const bands = buckets.map((bucket) => BUCKET_TO_BAND[bucket]).filter((band) => !!band);
	const isInactive = buckets.indexOf(REFRIGERANT_BUCKETS.INACTIVE) !== -1;

	return {
		...rest,
		isRefrigerantTracked: true,
		// `updateFilters` on the server force-injects isActive=true unless this is set, so every
		// bucket that must be able to surface an inactive appliance has to opt out of it — and then
		// say explicitly which side of the line it wants.
		noActiveFilter: true,
		isActive: isInactive ? 'false' : 'true',
		...(bands.length > 0 && { refrigerantComplianceBands: bands.join(',') }),
		...(buckets.indexOf(REFRIGERANT_BUCKETS.INSPECTION_DUE) !== -1 && {
			refrigerantInspectionDueBefore: nowMillis,
		}),
		...(buckets.indexOf(REFRIGERANT_BUCKETS.VERIFICATION_DUE) !== -1 && {
			refrigerantVerificationDueBefore: nowMillis,
		}),
	};
};

/**
 * The counts describe the whole tracked set, so the bucket the user is standing on must not narrow
 * them — otherwise selecting "Watch" drives the other five to zero. The server strips the same three
 * keys again on its side; this strips the frontend key they are derived from.
 */
export const withoutRefrigerantBucketSelection = (filters) => {
	const stripped = { ...(filters || {}) };
	delete stripped[REFRIGERANT_BUCKETS_FILTER_KEY];
	REFRIGERANT_DERIVED_FILTER_KEYS.forEach((key) => delete stripped[key]);
	return stripped;
};

/* ── Sorting ───────────────────────────────────────────────────────────────── */

/**
 * `sort_by` is matched against `AssetObj.IndexedColumn` names on the server, and an unrecognised one
 * falls through to the default name sort with NO error — so these strings have to match the server
 * exactly. They are the sort-only names (`refrigerantLeakRatePercent`), deliberately distinct from
 * the filter bounds of the same fields (`refrigerantLeakRateGreaterThan`), the way `daysInUse` is
 * distinct from `daysInUseGreaterThan`. See `AssetOpenSearchHelper.getSortQueries`.
 */
export const REFRIGERANT_ASSET_SORTERS = [
	{
		label: 'Highest leak rate',
		selectVal: 'leakRateDesc',
		value: { sort_by: 'refrigerantLeakRatePercent', order: 'descend' },
	},
	{
		label: 'Lowest leak rate',
		selectVal: 'leakRateAsc',
		value: { sort_by: 'refrigerantLeakRatePercent', order: 'ascend' },
	},
	{
		label: 'Inspection due soonest',
		selectVal: 'inspectionDueAsc',
		value: { sort_by: 'refrigerantNextInspectionAt', order: 'ascend' },
	},
	{
		label: 'Verification due soonest',
		selectVal: 'verificationDueAsc',
		value: { sort_by: 'refrigerantVerificationDueAt', order: 'ascend' },
	},
	{ label: 'Name', selectVal: 'nameAsc', value: { sort_by: 'name', order: 'ascend' } },
];

/**
 * The list's opening order: worst first, because the point of the screen is the appliances that are
 * over threshold. Exported rather than written inline on the page so the sort key exists in exactly
 * one place — a stale copy of it is precisely the bug that made these sorts silently fall back to
 * sorting by name.
 */
export const REFRIGERANT_ASSETS_DEFAULT_SORTER = REFRIGERANT_ASSET_SORTERS[0].value;

/**
 * The Event Log's chips. `name` is the wire value of the event type, because that is also the key
 * `countBy` stores its result under — the chip's label and its count would otherwise be looked up
 * with two different strings.
 *
 * They write into `eventTypes` (plural), which the ledger DAO reads as a comma-separated set, so
 * multi-select is a genuine OR here rather than the narrowing intersection the asset buckets give.
 */
export const REFRIGERANT_EVENT_TYPES_FILTER_KEY = 'eventTypes';

export const REFRIGERANT_EVENT_TYPE_PIPELINE_FILTERS = REFRIGERANT_EVENT_TYPE_ORDER.map(
	(eventType) => ({
		displayName: getRefrigerantEventTypeLabel(eventType),
		name: eventType,
		filter: { [REFRIGERANT_EVENT_TYPES_FILTER_KEY]: eventType },
		icon: undefined,
		iconStyle: undefined,
	})
);

/**
 * `occurredAt` is the ledger DAO's sort key for `occurred_at`; `id` is the tiebreaker.
 *
 * Was `occurredAtStartDate` — a range-filter BOUND that the DAO happened to also accept as a sort
 * case. The DAO now carries a dedicated `occurredAt` key and keeps the bounds as aliases, so this
 * asks for the column by its own name.
 */
export const REFRIGERANT_EVENT_SORTERS = [
	{
		label: 'Newest first',
		selectVal: 'occurredAtDesc',
		value: { sort_by: 'occurredAt', order: 'descend' },
	},
	{
		label: 'Oldest first',
		selectVal: 'occurredAtAsc',
		value: { sort_by: 'occurredAt', order: 'ascend' },
	},
];

/* ── Display formatting ────────────────────────────────────────────────────── */

/** 1 dp, matching the scale the server rounds the regulated number to. `--` when there is none. */
export const formatLeakRatePercent = (leakRatePercent) => {
	if (leakRatePercent === null || leakRatePercent === undefined || leakRatePercent === '')
		return '--';
	const numericRate = Number(leakRatePercent);
	return Number.isNaN(numericRate) ? '--' : `${numericRate.toFixed(1)}%`;
};

/**
 * Pounds, trimmed of trailing zeros — "41.5 lb", "52 lb", "0 lb". `0` is a real quantity and must
 * survive; only a genuinely absent value becomes `--`.
 */
export const formatPounds = (amountLb) => {
	if (amountLb === null || amountLb === undefined || amountLb === '') return '--';
	const numericAmount = Number(amountLb);
	if (Number.isNaN(numericAmount)) return '--';
	return `${parseFloat(numericAmount.toFixed(CHARGE_SCALE))} lb`;
};

/** Metric tonnes at 1 dp, the scale `RefrigerantLeakRateUtil` rounds CO2e to. */
export const formatCo2eTonnes = (co2eTonnes) => {
	if (co2eTonnes === null || co2eTonnes === undefined || co2eTonnes === '') return '--';
	const numericTonnes = Number(co2eTonnes);
	return Number.isNaN(numericTonnes) ? '--' : `${numericTonnes.toFixed(1)}t`;
};

/**
 * The GWP and the CO2e it produced, in one cell — "3922 73.8t", as the design prints it.
 *
 * Whole-number GWP: the reference table's values are integers on the IPCC AR4 basis, and a decimal
 * would imply a precision the basis does not carry.
 *
 * Either half can be absent independently. A refrigerant with no GWP on file still has no CO2e, but
 * an appliance whose GWP IS known can legitimately show no CO2e — that is what an appliance with no
 * charges yet looks like, and blanking the pair would hide the GWP that is genuinely on record.
 */
export const formatGwpAndCo2e = (gwp, co2eTonnes) => {
	const numericGwp = gwp === null || gwp === undefined || gwp === '' ? null : Number(gwp);
	const gwpLabel =
		numericGwp === null || Number.isNaN(numericGwp) ? null : `${Math.round(numericGwp)}`;
	const co2eLabel = formatCo2eTonnes(co2eTonnes);
	if (gwpLabel === null) return co2eLabel;
	return co2eLabel === '--' ? gwpLabel : `${gwpLabel} ${co2eLabel}`;
};

/** Mirrors `utils.RefrigerantLeakRateUtil.TrailingWindowMonths`. */
export const TRAILING_WINDOW_MONTHS = 12;

/**
 * Whether an event dated `occurredAt` lands inside the trailing 12-month leak-rate window.
 *
 * Mirrors the server's `eventsInWindow`: the window is `[asOf - 12 months, asOf]` with BOTH bounds
 * inclusive, so a charge dated exactly at the window start still counts and one a day earlier does
 * not. `asOf` defaults to now, which is what the summary on the drawer was computed against.
 *
 * This is the check that stops the drawer promising a leak-rate change a back-dated event cannot
 * cause. A charge from 13 months ago is real history and belongs in the ledger, but it contributes
 * nothing to the CURRENT 12-month numerator — the server drops it from `addedLbTrailing12Mo`, and a
 * projection that ignored the date would tell the tech the rate is about to jump when saving will
 * leave it exactly where it was.
 */
export const isWithinTrailingLeakRateWindow = (occurredAt, asOf = null) => {
	if (!occurredAt) return false;
	const asOfMoment = asOf ? moment(asOf) : moment();
	const eventMoment = moment(occurredAt);
	if (!eventMoment.isValid()) return false;
	const windowStart = asOfMoment.clone().subtract(TRAILING_WINDOW_MONTHS, 'months');
	return !eventMoment.isBefore(windowStart) && !eventMoment.isAfter(asOfMoment);
};

/**
 * The leak rate this appliance would read at if `additionalLb` more were charged into it today.
 *
 * Presentation only — it powers the drawer's "logging this takes the 12-month leak rate to X%"
 * warning so the tech sees the consequence before saving. The server recomputes the real number from
 * the ledger; this must never be written anywhere.
 *
 * Says nothing about WHEN the addition happened — callers must gate on
 * `isWithinTrailingLeakRateWindow` first, because an out-of-window charge adds nothing to this
 * window's numerator.
 */
export const projectLeakRatePercent = (addedLbTrailing12Mo, fullChargeLb, additionalLb) => {
	const fullCharge = Number(fullChargeLb);
	if (!fullCharge || Number.isNaN(fullCharge)) return null;
	const added = Number(addedLbTrailing12Mo) || 0;
	const additional = Number(additionalLb) || 0;
	return parseFloat((((added + additional) * 100) / fullCharge).toFixed(1));
};

/**
 * The refrigerant-tracked appliances on a work order, primary asset first.
 *
 * A work order carries one primary `asset` plus zero or more `subAssets`, and enrolment in the 608
 * programme is per unit (`isRefrigerantTracked`) — so a work order raised on a rack can easily have a
 * tracked sub-asset and an untracked primary, or the reverse. Both are hydrated on the work order, so
 * this needs no fetch.
 *
 * Used for two things that must agree: whether to show the work order's Refrigerant Tracking tab at
 * all, and which appliances that tab offers. Deriving them separately is how a tab appears with
 * nothing behind it.
 */
export const getRefrigerantTrackedAppliances = (workOrder) => {
	const primary = nullSafeGet('asset', workOrder);
	const subAssets = nullSafeGetOrElse('subAssets', workOrder, []);
	return [primary, ...subAssets].filter(
		(asset) => asset && nullSafeGet('id', asset) && nullSafeGet('isRefrigerantTracked', asset)
	);
};

/**
 * The picked calendar day as UTC midnight — what the log-event drawer sends as `occurredAt`.
 *
 * `Date` is a date-only field, and the ledger renders `occurredAt` in UTC on purpose: a compliance
 * record answers "which day was this appliance serviced", and rendering through the viewer's zone would
 * let one event show as two different days to two readers. But `moment.toISOString()` serialises LOCAL
 * midnight, so a tech at UTC+10 picking 12 August posted `11 August T14:00Z` and the ledger then
 * correctly displayed the wrong day. Sending the day itself, anchored at UTC midnight, is what makes
 * what the tech picked and what the record keeps the same date.
 *
 * The drawer's `disabledDate` is bounded on today IN UTC to match. That pairing is required, not
 * cosmetic: the server refuses a future `occurredAt`, so a picker bounded on the LOCAL day would let a
 * tech east of UTC choose a date whose UTC midnight had not arrived yet and get a 400.
 */
export const toUtcCalendarDay = (pickedDay) =>
	moment.utc(pickedDay.format('YYYY-MM-DD'), 'YYYY-MM-DD').toISOString();

/* ── The check-out gate ────────────────────────────────────────────────────── */

/**
 * This work order's ledger rows, as the check-out form reads them.
 *
 * Its own collection rather than `REFRIGERANT_ASSET_LEDGER_TC`: that one is scoped to ONE appliance
 * and exists to render the asset tab's servicing record, while this is scoped to the WORK ORDER
 * across every appliance on it. Sharing one would leave whichever mounted second rescoping the
 * other's list — and here that would mean the gate reading an appliance's whole history as though it
 * belonged to this job.
 */
export const REFRIGERANT_CHECK_OUT_EVENTS_TC = 'refrigerantCheckOutEvents';

/** The appliances the same form resolves by id — see `getWorkOrderAssetIds`. */
export const REFRIGERANT_CHECK_OUT_APPLIANCES_TC = 'refrigerantCheckOutAppliances';

/**
 * Every asset a work order names — its primary plus its sub-assets — as ids.
 *
 * IDS rather than the hydrated objects `getRefrigerantTrackedAppliances` reads, because the check-out
 * modal is opened from TWO surfaces carrying two different work-order shapes: the detail page passes
 * the hydrated MySQL read, and `WorkOrderTableRow` passes an OpenSearch document, which carries
 * `assetId` and `subAssetIds` and no asset objects at all. Reading the hydrated shape would answer
 * "no tracked appliance" on every table row, and a mandatory gate that is silently off on one of the
 * two ways in is worse than no gate at all.
 *
 * Both fields exist on both shapes, so this one derivation serves both. De-duplicated because a
 * work order's primary asset must not be counted twice if it also appears in its own `subAssetIds`.
 */
export const getWorkOrderAssetIds = (workOrder) => {
	const primaryAssetId = nullSafeGet('assetId', workOrder);
	const subAssetIds = nullSafeGetOrElse('subAssetIds', workOrder, []);
	return Array.from(
		new Set(
			[primaryAssetId, ...subAssetIds]
				.filter((assetId) => assetId !== null && assetId !== undefined && assetId !== '')
				.map(Number)
		)
	);
};

/**
 * Whether this check-out has to carry a 608 event before it is allowed through.
 *
 * Three conditions, and each one is load-bearing:
 *
 *  - **A tracked appliance is on the work order.** Enrolment is per unit, so "the asset uses
 *    refrigerant" means `isRefrigerantTracked`, the flag the asset form labels "Uses Refrigerant" —
 *    not the asset TYPE's `usesRefrigerant`, which only seeds it and stays true for a mini-split
 *    under the scope floor that has no ledger to write to.
 *  - **The ledger does not already reference this work order.** One event is the bar. A tech who
 *    logged a charge and a recovery has answered the question, and a second demand would be noise;
 *    a tech who logged nothing gets stopped. `loggedEventCount` counts rows filtered by
 *    `workOrderId`, so an event logged against the appliance on some OTHER job does not satisfy it.
 *  - **The check-out completes the work order.** Checking out as Parts on Order or Quote Required
 *    leaves the job open, so there is not necessarily a servicing event to record yet — the same
 *    reason the task-completion gate keys on `COMPLETED_CHECKOUT_VALUES` rather than on every
 *    check-out.
 *
 * The caller's own permission is NOT tested here: it decides whether to ask the question at all
 * (see `canLogRefrigerantEvents`) rather than what the answer is, and folding it in would make this
 * silently answer `false` for a third party whose appliance genuinely does need an event from
 * somebody.
 */
export const isRefrigerantEventRequiredForCheckOut = ({
	checkOutStatus,
	trackedApplianceCount,
	loggedEventCount,
}) =>
	(trackedApplianceCount || 0) > 0 &&
	(loggedEventCount || 0) === 0 &&
	COMPLETED_CHECKOUT_VALUES.includes(checkOutStatus);

/**
 * Whether this user can author a row in the 608 ledger at all.
 *
 * Two independent gates, and the check-out requirement has to respect BOTH — because blocking a
 * check-out on an action the user cannot perform is a work order nobody can close:
 *
 *  1. **Persona.** A third-party supplier cannot: `POST /asset/refrigerant_events` is mounted only on
 *     `supplier_internal_service_team.routes`, and the controller refuses any caller whose supplier
 *     company has no `privateBuyerCompanyId` — a third party must not author rows that drive another
 *     org's regulatory numbers.
 *  2. **Configured permission.** `MODIFY_ASSETS` is the role-level authorization every other
 *     Log Event surface already applies via `AccessPermissionChecker` — the ledger index
 *     (`RefrigerantEventLogPage`), the asset's own refrigerant tab (`AssetRefrigerantTrackingPage`).
 *     A user whose role denies it must not be shown the button here either, and — the reason this is
 *     folded into the gate rather than wrapped around the button — must not be BLOCKED at check-out
 *     by a requirement only somebody else can satisfy.
 *
 * `isUserAllowedToAccess` is the same buyer/supplier split `AccessPermissionChecker` makes; internal
 * techs are `userType` SUPPLIER, so they resolve against the supplier config as they do everywhere
 * else.
 */
export const canLogRefrigerantEvents = (currentUser, companyConfig) =>
	isBuyerOrInternalTech(currentUser) &&
	isUserAllowedToAccess(nullSafeGet('userType', currentUser))(
		PERMISSION_NAMES.MODIFY_ASSETS,
		companyConfig,
		nullSafeGetOrElse('roles', currentUser, [])
	);
